import styled, { css } from 'styled-components'

export interface CardRowProps {
  $columns?: string
  $mobileColumns?: string
  $minHeight?: string
  $gap?: string
  $padding?: string
  $mobilePadding?: string
  $direction?: 'rtl' | 'ltr'
  $isLast?: boolean
  $interactive?: boolean
  $hover?: boolean
}

const Root = styled.div<CardRowProps>`
  width: 100%;
  min-height: ${({ $minHeight }) => $minHeight ?? '4.5rem'};
  display: grid;
  grid-template-columns: ${({ $columns }) =>
    $columns ?? 'auto minmax(0, 1fr) auto'};
  align-items: center;
  gap: ${({ $gap }) => $gap ?? '1.25rem'};
  padding: ${({ $padding }) => $padding ?? '0.875rem 1.25rem'};
  direction: ${({ $direction }) => $direction ?? 'rtl'};
  background: ${({ theme }) => theme.colors.surface};
  border-bottom: 1px solid ${({ theme }) => theme.colors.line2};
  transition: background ${({ theme }) => theme.motion.fast} ease;

  @media (max-width: 540px) {
    grid-template-columns: ${({ $mobileColumns, $columns }) =>
      $mobileColumns ?? $columns ?? 'auto minmax(0, 1fr) auto'};
    gap: 0.5rem;
    padding: ${({ $mobilePadding }) => $mobilePadding ?? '0.625rem 0.5rem'};
  }

  ${({ $isLast }) =>
    $isLast &&
    css`
      border-bottom: 0;
    `}

  ${({ $hover = true, theme }) =>
    $hover &&
    css`
      /*
       * Touch devices keep :hover sticky after the first tap, which on
       * Android also eats the first swipe gesture (the row has to be
       * "woken up" before the swipe registers). Restrict the hover
       * effect to true pointer devices only.
       */
      @media (hover: hover) and (pointer: fine) {
        &:hover {
          background: ${theme.colors.line2};
        }
      }
    `}

  ${({ $interactive, theme }) =>
    $interactive &&
    css`
      cursor: pointer;
      &:focus-visible {
        outline: 2px solid ${theme.colors.accent};
        outline-offset: -2px;
      }
    `}
`

export type CellAlign = 'start' | 'center' | 'end' | 'stretch'

export interface CellProps {
  $align?: CellAlign
  $gap?: string
}

const alignMap: Record<CellAlign, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
}

const Cell = styled.div<CellProps>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $align }) => alignMap[$align ?? 'start']};
  justify-content: center;
  min-width: 0;
  gap: ${({ $gap }) => $gap ?? '0.25rem'};
`

export interface StackProps {
  $gap?: string
  $align?: CellAlign
}

const Stack = styled.div<StackProps>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $align }) => alignMap[$align ?? 'start']};
  gap: ${({ $gap }) => $gap ?? '0.25rem'};
  min-width: 0;
  max-width: 100%;
`

export interface LineProps {
  $gap?: string
  $wrap?: boolean
  $align?: CellAlign
}

const Line = styled.div<LineProps>`
  display: flex;
  align-items: ${({ $align }) => alignMap[$align ?? 'center']};
  gap: ${({ $gap }) => $gap ?? '0.5rem'};
  min-width: 0;
  max-width: 100%;
  ${({ $wrap }) =>
    $wrap &&
    css`
      flex-wrap: wrap;
    `}
`

export const CardRow = Object.assign(Root, { Cell, Stack, Line })

export default CardRow
